import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { User } from '@/gen-api/models';
// Import the workspace type from the generated models
import type { WorkspaceMemberResponseSerializerSchema } from '@/gen-api/models';

// Define a type for credentials similar to the example
// Note: Using simplified names 'accessToken' and 'refreshToken'
export interface AuthCredentials {
  accessToken: string;
  refreshToken: string;
}

export interface AuthState {
  credentials: AuthCredentials | null;
  userInfo: User | null;
  workspaces: WorkspaceMemberResponseSerializerSchema[] | null; // Store all user workspaces
  currentWorkspace: WorkspaceMemberResponseSerializerSchema | null; // Store the active workspace
  // Actions based on the user's example
  signIn: (
    credentials: AuthCredentials,
    userInfo: User | null, // Changed to non-optional for registration/login
    workspaces: WorkspaceMemberResponseSerializerSchema[] | null // Receive the array of workspaces
  ) => void;
  signOut: () => void;
  setUserInfo: (user: User | null) => void; // Keep this for potential separate updates
  setWorkspaces: (workspaces: WorkspaceMemberResponseSerializerSchema[] | null) => void; // Action to update workspaces list if needed
  setCurrentWorkspace: (workspace: WorkspaceMemberResponseSerializerSchema | null) => void; // Action to set the active workspace
  setCanCreateWorkspace: (canCreateWorkspace: boolean) => void; // Action to set the canCreateWorkspace flag
  canCreateWorkspace: boolean; // State to store the canCreateWorkspace flag
}

// Define the initial state separately
const initialState: Pick<
  AuthState,
  'credentials' | 'userInfo' | 'workspaces' | 'currentWorkspace' | 'canCreateWorkspace'
> = {
  credentials: null,
  userInfo: null,
  workspaces: null,
  currentWorkspace: null,
  canCreateWorkspace: false,
};

export const useAuthStore = createWithEqualityFn<AuthState>()(
  persist(
    (set, _get) => ({
      ...initialState,
      setCanCreateWorkspace: (canCreateWorkspace: boolean) => set({ canCreateWorkspace }),
      signIn: (
        credentials: AuthCredentials,
        userInfo: User | null,
        workspaces: WorkspaceMemberResponseSerializerSchema[] | null
      ) =>
        set({
          credentials,
          userInfo,
          workspaces,
          currentWorkspace: workspaces && workspaces.length > 0 ? workspaces[0] : null,
        }),
      signOut: () =>
        set({
          credentials: null,
          userInfo: null,
          workspaces: null,
          currentWorkspace: null,
        }),
      setUserInfo: (user: User | null) =>
        set((state) => ({
          ...state,
          userInfo: user,
        })),
      setWorkspaces: (workspaces: WorkspaceMemberResponseSerializerSchema[] | null) =>
        set((state) => {
          const currentStillExists =
            workspaces && state.currentWorkspace && workspaces.some((ws) => ws.id === state.currentWorkspace?.id);

          const adminWorkspaces = workspaces?.filter((ws) => ws.role === 'admin');

          return {
            ...state,
            workspaces,
            currentWorkspace: currentStillExists
              ? state.currentWorkspace
              : adminWorkspaces && adminWorkspaces.length > 0
                ? adminWorkspaces[0]
                : null,
          };
        }),
      setCurrentWorkspace: (workspace: WorkspaceMemberResponseSerializerSchema | null) =>
        set((state) => {
          if (workspace && !state.workspaces?.some((ws) => ws.id === workspace.id)) {
            console.warn("Attempted to set current workspace to one not in the user's list. Ignoring.");
            return state;
          }
          return {
            ...state,
            currentWorkspace: workspace,
          };
        }),
    }),
    {
      name: 'auth-storage',
      partialize: (
        state: AuthState
      ): Pick<AuthState, 'credentials' | 'userInfo' | 'workspaces' | 'currentWorkspace'> => ({
        credentials: state.credentials,
        userInfo: state.userInfo,
        workspaces: state.workspaces,
        currentWorkspace: state.currentWorkspace,
      }),
    }
  ),
  shallow
);
